import {computed, ref} from "vue";
import {
    NotificationSequenceable,
    NotificationSequenceDetails
} from "@/notification-sequences/interfaces/NotificationSequence";
import {notificationSequenceApi} from "@/container";
import {
    NotificationSequenceIndexQueryParams
} from "@/core/http/resources/notification-sequence/NotificationSequenceResource";
import useSwr from "@/core/composables/swr/useSwr";
import {filterEmptyKeys} from "@/lib/utils";

export interface SequenceSection {
    tag: NotificationSequenceable | null;
    sequences: NotificationSequenceDetails[];
}

const BASE_PARAMS: NotificationSequenceIndexQueryParams = {
    with: 'templates',
    types: ['company', 'laboratory', 'program']
};

export const useRcsSequences = () => {
    const selectedTag = ref<NotificationSequenceable | null>(null);
    const refreshing = ref(false);

    const {data: tags} = useSwr(
        'rcs-sequence-tags',
        () => notificationSequenceApi().tags()
    );

    const sequencesParams = computed<NotificationSequenceIndexQueryParams>(() => {
        if (!selectedTag.value) {
            return BASE_PARAMS;
        }

        return {...BASE_PARAMS, ...buildTagParams(selectedTag.value)};
    });

    const sequencesKey = computed(() => {
        const query = new URLSearchParams(filterEmptyKeys(sequencesParams.value));

        return `notification-sequences?${query.toString()}`;
    });

    const {data: sequences, isLoading: sequencesLoading} = useSwr(
        sequencesKey,
        () => notificationSequenceApi()
            .index<NotificationSequenceIndexQueryParams & {with: 'templates'}>({
                ...sequencesParams.value,
                with: 'templates',
            })
            .finally(() => refreshing.value = false)
    );

    const initialLoading = computed(() => sequencesLoading.value && !sequences.value);

    /**
     * Sections of sequences grouped by sequenceable tag. A sequence attached to
     * several sequenceables is duplicated in each matching section; sequences
     * matching no tag end up in a trailing untagged section.
     */
    const sections = computed<SequenceSection[]>(() => {
        const sequenceList = sequences.value ?? [];
        const tagList = tags.value ?? [];

        if (selectedTag.value) {
            return [{tag: selectedTag.value, sequences: sequenceList}];
        }

        const tagSections = tagList
            .map(tag => ({
                tag,
                sequences: sequenceList.filter(
                    sequence => sequence.sequenceables?.some(s => s.type === tag.type && s.id === tag.id)
                ),
            }))
            .filter(section => section.sequences.length > 0);

        const groupedIds = new Set(tagSections.flatMap(section => section.sequences.map(s => s.id)));
        const untaggedSequences = sequenceList.filter(sequence => !groupedIds.has(sequence.id));

        if (untaggedSequences.length > 0) {
            return [...tagSections, {tag: null, sequences: untaggedSequences}];
        }

        return tagSections;
    });

    /**
     * Builds the index query params for a given tag,
     * mapping the sequenceable type to the corresponding id param.
     */
    const buildTagParams = (tag: NotificationSequenceable): Partial<NotificationSequenceIndexQueryParams> => {
        if (tag.type === 'survey') {
            return {survey_id: tag.id};
        }

        return {program_id: tag.id};
    };

    /**
     * Selects a tag — swrv reacts to the key change and refetches if not cached.
     */
    const selectTag = (tag: NotificationSequenceable | null) => {
        refreshing.value = true;
        selectedTag.value = tag;
    };

    return {
        tags,
        selectedTag,
        sections,
        initialLoading,
        sequencesLoading,
        selectTag,
        refreshing
    };
};
